import {
  OrganizationMembership,
  useOnboardingStepsCompleted,
  useUserPreferences
} from "@/api";
import { TypographySM } from "@/components/typography/SM";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Role } from "@/gql/__generated__/graphql";
import { SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST } from "@/lib/white-label-branding/BrandLabelConstants";
import { getWhiteLabelBrand } from "@/lib/white-label-branding/getWhiteLabelBrand";
import { NavigateOptions, useRouter, useSearch } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

type Props = {
  organizationMemberships: OrganizationMembership[];
  setLatestOrganizationSlug: (slug: string) => void;
};

export const SelectableOrganizations = ({
  organizationMemberships,
  setLatestOrganizationSlug
}: Props) => {
  const { navigate } = useRouter();
  const searchParams = useSearch({ from: "/_auth/organization" });
  const { isOnboardingCompleted } = useOnboardingStepsCompleted();
  const { data: preferences } = useUserPreferences();
  const brand = getWhiteLabelBrand();

  return organizationMemberships?.map((membership) => (
    <button
      className="flex justify-between items-center cursor-pointer text-sm w-full p-2 border-b transition-colors hover:bg-muted/50"
      onClick={() => {
        setLatestOrganizationSlug(membership.organization.slug);

        let to: NavigateOptions["to"] =
          "/organization/$organizationSlug/gallery";

        if (
          membership?.role === Role.Admin &&
          !isOnboardingCompleted &&
          !preferences?.onboardingDisabled &&
          brand?.hostname !== SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST
        )
          to = "/organization/$organizationSlug/onboarding";

        if (searchParams?.redirect)
          to =
            `/organization/$organizationSlug/${searchParams.redirect}` as NavigateOptions["to"];

        navigate({
          to,
          params: { organizationSlug: membership.organization.slug },
          search: searchParams?.search
        }).then(() => {
          toast({
            title: "Switched organization",
            description: `You are now using the ${membership.organization.name} organization.`
          });
        });
      }}
      key={membership.organization.id}
    >
      <div className="flex items-center gap-2">
        <TypographySM data-testid="selectableOrganizationName">
          {membership.organization.name}
        </TypographySM>
        <Badge variant="outline" className="capitalize">
          {membership.role.toLowerCase()}
        </Badge>
      </div>
      <ArrowRight size={16} />
    </button>
  ));
};
